import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SessionExpiryDialog from '../components/common/SessionExpiryDialog';
import { cancelPaymentIntent, clearTerminal } from '../utils/paymentUtils';
import { useAppContext } from './AppContext';
import { useKeyboard } from './KeyboardContext';

const SessionTimeoutContext = createContext(null);

// Session inactivity timeout (separate from payment terminal timeout)
const DEFAULT_TIMEOUT_MS = Number(process.env.REACT_APP_SESSION_TIMEOUT_MS || 60000);
const EXTEND_MS = Number(process.env.REACT_APP_SESSION_EXTEND_MS || 30000); // dialog countdown duration and expiry offset

export const SessionTimeoutProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearSession } = useAppContext?.() || { clearSession: () => {} };
  const kb = useKeyboard();

  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(Math.max(1, Math.floor(EXTEND_MS / 1000)));
  // When true, the SessionExpiryDialog UI will not render, but timers still run
  const [suppressed, setSuppressed] = useState(false);

  const timeoutMsRef = useRef(DEFAULT_TIMEOUT_MS);
  // Track current configured timeout
  const activePIRef = useRef(null);
  // True while the global inactivity timeout is paused by a page-scoped flow (e.g. the Zelle
  // dialog, which runs its own 5-minute timer). While paused, timers are stopped and NOT re-armed.
  const suppressedRef = useRef(false);

  const warningTimerRef = useRef(null);
  const expiryTimerRef = useRef(null);
  const finalCountdownRef = useRef(null);

  const resetInternalTimers = useCallback(() => {
    if (warningTimerRef.current) { clearTimeout(warningTimerRef.current); warningTimerRef.current = null; }
    if (expiryTimerRef.current) { clearTimeout(expiryTimerRef.current); expiryTimerRef.current = null; }
    if (finalCountdownRef.current) { clearInterval(finalCountdownRef.current); finalCountdownRef.current = null; }
  }, []);

  // Define expire BEFORE start so start can depend on it safely
  const expire = useCallback(async () => {
    resetInternalTimers();
    setWarningOpen(false);
    try { console.log('[SessionTimeout] Expire triggered. Navigating to /home'); } catch {}
    // Close any on-screen keyboard if visible
    try { kb?.close?.(); } catch {}
    const pi = activePIRef.current;
    // Clear terminal only if we had an active card payment (avoid when user only used Zelle)
    if (pi && String(pi).startsWith('pi_')) {
      try {
        await clearTerminal(null);
        console.log('[SessionTimeout] Terminal cleared on session timeout');
      } catch (e) {
        console.warn('[SessionTimeout] Terminal clear failed (ignored):', e?.message);
      }
      try { await cancelPaymentIntent(pi); } catch (e) { /* noop */ }
    }
    try { clearSession?.(); } catch (e) { /* noop */ }
    navigate('/home', { replace: true });
  }, [navigate, resetInternalTimers, clearSession, kb]);

  // Define start BEFORE noteActivity to avoid TDZ issues (and after expire so deps are initialized)
  const start = useCallback((ms) => {
    // Paused (e.g. Zelle dialog open): never arm the inactivity timers while suppressed.
    if (suppressedRef.current) { resetInternalTimers(); setWarningOpen(false); return; }
    const total = Number(ms || process.env.REACT_APP_SESSION_TIMEOUT_MS || DEFAULT_TIMEOUT_MS);
    timeoutMsRef.current = total;
    resetInternalTimers();
    setWarningOpen(false);
    try { console.log('[SessionTimeout] Start timers for path', location.pathname, 'timeout(ms)=', total, 'extend(ms)=', EXTEND_MS); } catch {}

    // Show warning only AFTER full inactivity period
    warningTimerRef.current = setTimeout(() => {
      setWarningOpen(true);
      try { console.log('[SessionTimeout] Warning opened for path', location.pathname); } catch {}
      // Begin countdown (EXTEND_MS in seconds)
      let remaining = Math.max(1, Math.floor(EXTEND_MS / 1000));
      setSecondsRemaining(remaining);
      if (finalCountdownRef.current) clearInterval(finalCountdownRef.current);
      finalCountdownRef.current = setInterval(() => {
        remaining -= 1;
        setSecondsRemaining(remaining);
        if (remaining <= 0) {
          clearInterval(finalCountdownRef.current);
          finalCountdownRef.current = null;
        }
      }, 1000);
    }, total);

    // Expire after EXTEND_MS once the warning opens
    expiryTimerRef.current = setTimeout(() => {
      void expire();
    }, total + EXTEND_MS);
  }, [resetInternalTimers, expire]);

  // Kiosk routes to watch
  const watchedRoutes = useMemo(() => (
    ['/shop', '/checkout', '/checkout/new-devotee', '/payment', '/confirmation']
  ), []);

  const isWatchedPath = useCallback((path) => {
    return watchedRoutes.some(p => path.startsWith(p));
  }, [watchedRoutes]);

  const noteActivity = useCallback(() => {
    // Paused (e.g. Zelle dialog open): do not let a tap re-arm the inactivity timers.
    if (suppressedRef.current) return;
    // Ignore activity when not on a watched kiosk route (e.g., home page)
    if (!isWatchedPath(location.pathname)) return;
    // If warning is open, do not auto-reset; require explicit Extend
    setWarningOpen(prev => {
      if (!prev) {
        // Restart timers relative to this activity
        start(timeoutMsRef.current);
      }
      return prev;
    });
  }, [start, isWatchedPath, location.pathname]);

  const extend = useCallback((ms) => {
    // User explicitly extends; close dialog and fully restart timers without navigation
    resetInternalTimers();
    setWarningOpen(false);
    setSecondsRemaining(Math.max(1, Math.floor(EXTEND_MS / 1000)));
    start(ms || timeoutMsRef.current);
  }, [resetInternalTimers, start]);

  const cancel = useCallback(() => {
    resetInternalTimers();
    setWarningOpen(false);
  }, [resetInternalTimers]);

  // Pause/resume the global inactivity timeout (and hide its warning UI while paused).
  // Pausing STOPS the timers and, via suppressedRef, prevents start()/noteActivity() from
  // re-arming them — so a flow with its own timer (the Zelle 5-min dialog) is not cut short by
  // the ~90s session expiry. Resuming lets the caller re-arm with start().
  const suppressWarnings = useCallback((flag) => {
    const on = Boolean(flag);
    suppressedRef.current = on;
    setSuppressed(on);
    if (on) {
      resetInternalTimers();
      setWarningOpen(false);
    }
  }, [resetInternalTimers]);

  const registerActivePaymentIntent = useCallback((paymentIntentId) => {
    activePIRef.current = paymentIntentId || null;
  }, []);

  const clearActivePaymentIntent = useCallback(() => {
    activePIRef.current = null;
  }, []);

  // (moved expire above start)

  // Global activity listeners
  useEffect(() => {
    const onAny = () => noteActivity();
    document.addEventListener('click', onAny, { passive: true });
    document.addEventListener('touchstart', onAny, { passive: true });
    document.addEventListener('keydown', onAny, { passive: true });
    return () => {
      document.removeEventListener('click', onAny);
      document.removeEventListener('touchstart', onAny);
      document.removeEventListener('keydown', onAny);
    };
  }, [noteActivity]);

  // Reset/start on route change within kiosk flow
  useEffect(() => {
    // Only apply to kiosk shopping/payment routes
    const path = location.pathname;
    // A route change ends any page-scoped pause (e.g. the Zelle dialog on /payment), so the new
    // page gets a normal inactivity timeout instead of staying frozen.
    suppressedRef.current = false;
    setSuppressed(false);
    if (isWatchedPath(path)) {
      start();
    } else {
      cancel();
    }
  }, [location.pathname, start, cancel, isWatchedPath]);

  // Log suppression toggles to debug hidden dialog
  useEffect(() => {
    try { console.log('[SessionTimeout] Suppressed UI =', suppressed, 'at path', location.pathname); } catch {}
  }, [suppressed, location.pathname]);

  const value = useMemo(() => ({
    start,
    extend,
    cancel,
    noteActivity,
    registerActivePaymentIntent,
    clearActivePaymentIntent,
    warningOpen,
    secondsRemaining,
    expire,
    suppressWarnings,
  }), [start, extend, cancel, noteActivity, registerActivePaymentIntent, clearActivePaymentIntent, warningOpen, secondsRemaining, expire, suppressWarnings]);

  return (
    <SessionTimeoutContext.Provider value={value}>
      {children}
      {isWatchedPath(location.pathname) && !suppressed && (
        <SessionExpiryDialog
          open={warningOpen}
          secondsRemaining={secondsRemaining}
          onExtend={() => extend()}
          onEnd={() => expire()}
        />
      )}
    </SessionTimeoutContext.Provider>
  );
};

export const useSessionTimeout = () => useContext(SessionTimeoutContext);
